import {useEffect, useMemo, useState} from 'react';

const DEFAULT_LANGUAGE = 'en';

const normalizeLanguages = (languages: string[] = []): string[] => {
  const values = languages.length ? languages : [DEFAULT_LANGUAGE];
  const withEnglish = values.includes(DEFAULT_LANGUAGE)
    ? values
    : [...values, DEFAULT_LANGUAGE];

  return [...new Set(withEnglish)].sort();
};

export const useSupportedLanguages = (
  initialLanguages: string[] = [DEFAULT_LANGUAGE],
) => {
  const normalizedInitialLanguages = useMemo(
    () => normalizeLanguages(initialLanguages),
    [JSON.stringify(initialLanguages)],
  );

  const normalizedInitialLanguagesKey = useMemo(
    () => normalizedInitialLanguages.join('|'),
    [normalizedInitialLanguages],
  );

  const [selected, setSelected] = useState<string[]>(
    normalizedInitialLanguages,
  );

  useEffect(() => {
    setSelected(prev => {
      const prevKey = prev.join('|');
      return prevKey === normalizedInitialLanguagesKey
        ? prev
        : normalizedInitialLanguages;
    });
  }, [normalizedInitialLanguages, normalizedInitialLanguagesKey]);

  const isDirty = useMemo(() => {
    return selected.join('|') !== normalizedInitialLanguagesKey;
  }, [selected, normalizedInitialLanguagesKey]);

  const toggleLanguage = (langValue: string): void => {
    if (langValue === DEFAULT_LANGUAGE) {
      return;
    }

    setSelected(prev => {
      const next = prev.includes(langValue)
        ? prev.filter(item => item !== langValue)
        : [...prev, langValue];

      return normalizeLanguages(next);
    });
  };

  const handleClearAll = (): void => {
    setSelected([DEFAULT_LANGUAGE]);
  };

  const resetState = (): void => {
    setSelected(normalizedInitialLanguages);
  };

  return {
    selected,
    isDirty,
    toggleLanguage,
    handleClearAll,
    resetState,
  };
};
